import { ArrowRight, MapPin, Sparkles, Star } from 'lucide-react';
import { ArenaStatus, FeaturedArena } from 'modules/jogador/home/content';
import { Card } from 'shared/components/ui/Card';
import { Badge } from 'shared/components/ui/Badge';
import { Button } from 'shared/components/ui/Button';
import { cn } from 'shared/utils/cn';
import { Link } from 'react-router-dom';
import { ROUTES } from 'shared/constants/routes';

interface ArenaCardProps {
  arena: FeaturedArena;
}

const STATUS_TONE: Record<ArenaStatus, 'success' | 'accent' | 'warning'> = {
  'Aberto agora': 'success',
  'Vagas hoje': 'accent',
  'Torneio ativo': 'warning',
};

export function ArenaCard({ arena }: ArenaCardProps) {
  return (
    <Card
      className={cn(
        'group flex h-full flex-col transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/40',
        arena.isNew && 'border-primary/50 ring-2 ring-primary/15'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <Badge tone={STATUS_TONE[arena.status]}>{arena.status}</Badge>
        {arena.isNew ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-primary-dark">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Nova na plataforma
          </span>
        ) : (
          <span className="flex items-center gap-1 text-sm font-medium text-ink">
            <Star className="h-4 w-4 fill-warning text-warning" aria-hidden="true" />
            {arena.rating.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}{' '}
            <span className="text-ink-secondary">({arena.reviewCount})</span>
          </span>
        )}
      </div>

      <div className="mt-3 flex items-start justify-between gap-2">
        <h3 className="font-heading text-lg font-bold text-ink transition-colors group-hover:text-primary-dark">{arena.name}</h3>
        <Badge tone="neutral" className="flex-shrink-0">
          {arena.neighborhood}
        </Badge>
      </div>

      <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-secondary">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {arena.address}
      </p>

      <div className="mb-4 mt-3 flex flex-wrap gap-2">
        {arena.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-border px-3 py-1 text-xs text-ink-secondary transition-colors group-hover:border-primary/30 group-hover:bg-accent-soft/40"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* mt-auto: o rodapé (preço + CTA) fica sempre no pé do card, alinhado entre os cards. */}
      <div className="mt-auto flex items-center justify-between border-t border-border/40 pt-4">
        <div>
          <p className="text-xs text-ink-secondary">A partir de</p>
          <p className="font-heading text-lg font-bold text-ink">R$ {arena.priceFrom}/h</p>
        </div>
        <Link to={ROUTES.BOOKING}>
          <Button>
            Reservar
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}
